<script setup lang="ts">
import { debounce, truncate } from 'lodash'
import type { IFornecedor } from '../admin/fornecedor/types'
import { useLandingPageStore } from '../landing-page/store/useLandingPageStore'
import FornecedorDetalhesDialog from '@/pages/admin/pesquisa-avancada/components/FornecedorDetalhesDialog.vue'

definePage({
  meta: {
    layout: 'landing-page',
    public: true,
  },
})

const store = useLandingPageStore()

// Dados reativos
const showMobileFilters = ref(false)

const {
  fetchSearchBoxSuggest,
  clearSearchBoxSuggest,
  fetchSearchSpaceEvents,
} = store

const {
  suppliersInfinite,
  data,
  searchText,
  searchBoxSuggest,
  loading,
  suppliers,
  categoriesSupplier,
  categorySpaces,
  categoryExperiences,
  selectedSupplier,
  dialogSupplierDetail,
  suppliersTotal,
  suppliersPage,
} = storeToRefs(store)

const onInput = debounce(value => {
  if (value)
    fetchSearchBoxSuggest(value)
  else
    clearSearchBoxSuggest()
}, 300)

const isDisabledSearchBtn = computed(() => {
  console.log(data.value)

  if (data.value.category_type === 'suplicy') {
    return (
      isEmpty(data.value.date)
      || isEmpty(data.value.category_slug)
      || isEmpty(data.value.category_type)
      || isEmpty(data.value.region)
      || isEmpty(data.value.capacity)
    )
  }

  return (
    isEmpty(data.value.category_slug)
    || isEmpty(data.value.category_type)
    || isEmpty(data.value.region)
  )
})

const onSearch = async () => {
  suppliersPage.value = 1
  suppliersTotal.value = 0

  suppliers.value = []

  await store.fetchSearchSpaceEvents({ done: () => {} })
}

const navbarItems = computed(() => {
  return [
    ...categoriesSupplier.value,
    ...categorySpaces.value,
    ...categoryExperiences.value,
  ]
})

// Métodos
const viewSupplierDetails = async (supplier: IFornecedor) => {
  selectedSupplier.value = supplier
  dialogSupplierDetail.value = true

  // Registrar visualização
  const { getSessionId } = await import('@/utils/sessionManager.ts')
  const AcoesService = (await import('@/services/acoesService.ts')).default

  const sessionId = getSessionId()

  console.log('🔍 [PESQUISAR] Registrando visualização:', {
    fornecedorId: supplier.id,
    sessionId,
    termo_busca: {
      category_slug: data.value.category_slug,
      category_type: data.value.category_type,
      date: data.value.date,
      capacity: data.value.capacity,
      region: data.value.region,
    },
  })

  try {
    await AcoesService.registrarAcao(supplier.id!, {
      tipo_acao: 'visualizacao_detalhes',
      session_id: sessionId,
      termo_busca: {
        category_slug: data.value.category_slug,
        category_type: data.value.category_type,
        date: data.value.date,
        capacity: data.value.capacity,
        region: data.value.region,
      },
    })
    console.log('✅ [PESQUISAR] Visualização registrada com sucesso!')
  }
  catch (error) {
    console.error('❌ [PESQUISAR] Erro ao registrar visualização:', error)
  }
}

const applyMobileFilters = () => {
  showMobileFilters.value = false

  onSearch()
}
</script>

<template>
  <div class="search-page">
    <VContainer
      fluid
      class="pa-0"
    >
      <h2 class="text-h4 py-5 mb-4 font-weight-bold">
        Resultado de pesquisa
      </h2>

      <VRow class="mx-0 mb-4">
        <!-- Sidebar com filtros -->
        <VCol
          cols="12"
          lg="3"
          class="pa-0 d-none d-lg-block"
        >
          <VCard
            flat
            class="h-100 mr-4 navigation-drawer"
          >
            <VNavigationDrawer
              absolute
              permanent
              width="100%"
              :style="{
                height: '100% !important',
              }"
            >
              <VList
                class="pa-0"
                :style="{
                  height: '85vh !important',
                }"
              >
                <VListItem
                  v-for="(item, i) in navbarItems"
                  :key="i"
                  :value="item"
                  color="primary"
                  class="px-4"
                  :active="data.category?.slug === item.slug"
                  @click="() => {
                    data.category = item
                    data.category_slug = item.slug
                    data.category_type = item.type
                    onSearch()
                  }"
                >
                  <template #prepend>
                    <VIcon
                      color="primary"
                      size="30"
                      :icon="item?.icon_formatted ?? 'tabler-favicon'"
                    />
                  </template>

                  <VListItemTitle class="text-primary">
                    {{ item?.descricao }}
                  </VListItemTitle>
                </VListItem>
              </VList>
            </VNavigationDrawer>
          </VCard>
        </VCol>

        <!-- Área principal -->
        <VCol
          cols="12"
          lg="9"
          class="pa-0"
        >
          <div class="main-content">
            <!-- Área de filtros superiores -->
            <VCard
              v-if="$vuetify.display.mdAndUp"
              class="input-area rounded"
            >
              <VCardText class="pa-2">
                <VRow
                  class="d-flex flex-wrap align-center"
                  align="center"
                  justify="space-between"
                  no-gutters
                >
                  <VCol
                    cols="12"
                    sm="6"
                    md="2"
                    class="pb-2 pb-md-0 px-1"
                  >
                    <VTextField
                      v-model="data.date"
                      :disabled="data.category?.type !== 'space-events'"
                      type="date"
                      variant="outlined"
                      placeholder="Data"
                      class="app-input"
                      hide-details
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    sm="6"
                    md="4"
                    class="pb-2 pb-md-0 px-1"
                  >
                    <VAutocomplete
                      v-model="data.category"
                      :items="navbarItems"
                      variant="outlined"
                      placeholder="O que procura?"
                      class="app-search"
                      item-title="descricao"
                      no-data-text="Nenhuma categoria foi encontrada"
                      hide-details
                      return-object
                      @update:model-value="(item) => {
                        data.category_slug = item.slug
                        data.category_type = item.type
                      }"
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    sm="6"
                    md="3"
                    class="pb-2 pb-md-0 px-1"
                  >
                    <VMenu>
                      <template #activator="{ props }">
                        <VTextField
                          v-model="searchText"
                          variant="outlined"
                          placeholder="Qual a localização?"
                          class="app-input"
                          v-bind="props"
                          :loading="loading.searchBoxSuggest"
                          hide-details
                          @update:model-value="onInput"
                        />
                      </template>
                      <VList>
                        <VListItem
                          v-for="(item, index) in searchBoxSuggest"
                          :key="index"
                          @click="() => {
                            searchText = item.text;
                            data.region = item.value;
                          }"
                        >
                          <VListItemTitle>{{ item.text }}</VListItemTitle>
                        </VListItem>
                        <VListItem
                          v-if="searchBoxSuggest.length === 0"
                          class="text-center"
                        >
                          <VListItemTitle>Nenhuma localização foi encontrada</VListItemTitle>
                        </VListItem>
                      </VList>
                    </VMenu>
                  </VCol>
                  <VCol
                    cols="12"
                    sm="5"
                    md="2"
                    class="pb-2 pb-md-0 px-1"
                  >
                    <VTextField
                      v-model="data.capacity"
                      :disabled="data.category_type !== 'space-events'"
                      type="number"
                      variant="outlined"
                      placeholder="Convidados"
                      class="app-input"
                      hide-details
                    />
                  </VCol>
                  <div
                    class="pb-2 pb-md-0 px-1 text-center text-md-left"
                    :style="{
                      width: $vuetify.display.smAndDown ? '100%' : 'auto',
                    }"
                  >
                    <VBtn
                      v-if="!searchSpaceEventsLoading"
                      color="primary"
                      :block="$vuetify.display.smAndDown"
                      variant="flat"
                      :size="$vuetify.display.smAndDown ? 'large' : $vuetify.display.mdAndUp ? 40 : 48"
                      @click="onSearch"
                    >
                      <VIcon icon="tabler-search" />
                    </VBtn>
                  </div>
                </VRow>
              </VCardText>
            </VCard>
            <VCard
              v-else
              class="input-area rounded"
            >
              <VCardText class="pa-2">
                <VAutocomplete
                  v-model="data.category_slug"
                  :items="navbarItems"
                  variant="outlined"
                  placeholder="O que procura?"
                  class="app-search app-search-mobile"
                  item-value="slug"
                  item-title="descricao"
                  no-data-text="Nenhuma categoria foi encontrada"
                  hide-details
                >
                  <template #append>
                    <IconBtn @click="onSearch">
                      <VIcon
                        color="primary"
                        icon="tabler-search"
                      />
                    </IconBtn>
                  </template>
                </VAutocomplete>

                <VDivider class="my-4" />

                <div class="d-flex gap-2">
                  <VAutocomplete
                    v-model="data.category_type"
                    :items="[
                      { slug: 'suplicy', descricao: 'Fornecedores' },
                      { slug: 'space-events', descricao: 'Espaços para eventos' },
                      { slug: 'experience', descricao: 'Experiências' },
                    ]"
                    variant="outlined"
                    placeholder="Tipo de serviço"
                    class="app-search"
                    item-value="slug"
                    item-title="descricao"
                    no-data-text="Nenhuma tipo de serviço foi encontrado"
                    hide-details
                  />

                  <VBtn
                    variant="text"
                    color="primary"
                    @click="showMobileFilters = true"
                  >
                    Filtros
                    <VIcon icon="tabler-filter" />
                  </VBtn>
                </div>
              </VCardText>
            </VCard>

            <!-- Grid de resultados -->
            <div
              v-if="suppliers.length > 0"
              class="results-grid my-4"
            >
              <VInfiniteScroll
                v-if="suppliers.length > 0"
                ref="suppliersInfinite"
                color="secondary"
                mode="manual"
                class="supplier-content flex-row flex-wrap my-2"
                @load="fetchSearchSpaceEvents"
              >
                <VCol
                  v-for="(supplier, index) in suppliers"
                  :key="index"
                  cols="12"
                  sm="6"
                  md="4"
                >
                  <VCard
                    hover
                    class="supplier-card h-100"
                    elevation="2"
                  >
                    <VImg
                      :src="supplier.destaque ?? 'https://placehold.co/1920x1080'"
                      eager
                      class="card-image supplier-image"
                      aspect-ratio="16/9"
                      max-height="200"
                    />

                    <VCardText class="d-flex flex-column pa-3">
                      <h3 class="text-subtitle-1 font-weight-bold mb-2">
                        {{ supplier.nome_fantasia }}
                      </h3>

                      <p class="text-body-2 text-grey-darken-1 mb-3">
                        {{
                          truncate(supplier.descricao, {
                            length: 150,
                            separator: ' ',
                          })
                        }}
                      </p>

                      <VSpacer />

                      <div>
                        <VBtn
                          variant="outlined"
                          color="primary"
                          size="small"
                          block
                          @click="viewSupplierDetails(supplier)"
                        >
                          Ver Informações
                        </VBtn>
                      </div>
                    </VCardText>
                  </VCard>
                </VCol>

                <template #load-more="{ props }">
                  <VBtn
                    v-if="suppliers.length !== suppliersTotal"
                    v-bind="props"
                    color="primary"
                    width="150"
                    variant="outlined"
                    class="px-2 text-sm"
                    size="sm"
                  >
                    Ver mais
                  </VBtn>
                </template>
                <template #empty />
              </VInfiniteScroll>
            </div>

            <VEmptyState
              v-else
              class="supplier-empty my-4"
              icon="tabler-search"
              title="Nenhum resultado encontrado"
              size="80"
              color="primary"
              subtitle="Tente novamente com outras palavras-chave."
            />
          </div>
        </VCol>
      </VRow>
    </VContainer>

    <!-- Dialog de filtros mobile -->
    <VDialog
      v-model="showMobileFilters"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <VCard>
        <VToolbar>
          <VBtn
            icon="tabler-x"
            @click="showMobileFilters = false"
          />

          <VToolbarTitle>Filtros</VToolbarTitle>

          <VSpacer />

          <VBtn
            variant="text"
            @click="applyMobileFilters"
          >
            Aplicar
          </VBtn>
        </VToolbar>

        <VCardText>
          <VTextField
            v-model="data.date"
            :disabled="data.category_type !== 'space-events'"
            type="date"
            variant="outlined"
            placeholder="Data"
            class="app-input my-1"
            hide-details
          />
          <VMenu>
            <template #activator="{ props }">
              <VTextField
                v-model="searchText"
                variant="outlined"
                placeholder="Qual a localização?"
                class="app-input my-1"
                v-bind="props"
                :loading="loading.searchBoxSuggest"
                hide-details
                @update:model-value="onInput"
              />
            </template>
            <VList>
              <VListItem
                v-for="(item, index) in searchBoxSuggest"
                :key="index"
                @click="() => {
                  searchText = item.text;
                  data.region = item.value;
                }"
              >
                <VListItemTitle>{{ item.text }}</VListItemTitle>
              </VListItem>
              <VListItem
                v-if="searchBoxSuggest.length === 0"
                class="text-center"
              >
                <VListItemTitle>Nenhuma localização foi encontrada</VListItemTitle>
              </VListItem>
            </VList>
          </VMenu>
          <VTextField
            v-model="data.capacity"
            :disabled="data.category_type !== 'space-events'"
            type="number"
            variant="outlined"
            placeholder="Convidados"
            class="app-input my-1"
            hide-details
          />
        </VCardText>
      </VCard>
    </VDialog>

    <!-- Dialog de Detalhes do Fornecedor -->
    <FornecedorDetalhesDialog
      v-model="dialogSupplierDetail"
      :fornecedor="selectedSupplier"
      @fechar="dialogSupplierDetail = false"
    />
  </div>
</template>

<style lang="scss">
.app-search-mobile {
  .v-input__append {
    margin: 0 5px;
  }
}

.supplier-content {
  .v-infinite-scroll__side {
    display: none;
  }

  .v-infinite-scroll__side {
    display: block;
    inline-size: 100%;
    text-align: center;
  }
}

.supplier-empty {
  block-size: calc(100% - 25%);
}

.supplier-card {
  display: flex;
  flex-direction: row;
  column-gap: 20px;

  .supplier-content {
    flex: 1;
  }
}
</style>

<style lang="scss" scoped>
.navigation-drawer {
  .v-navigation-drawer {
    .v-list {
      overflow: auto;
    }

    /* stylelint-disable-next-line liberty/use-logical-spec */
    top: 0 !important;
    border: none;
    inline-size: 100%;
  }
}

.search-header {
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 10%);
}

.search-sidebar {
  background-color: white;
  block-size: calc(100vh - 64px);
  border-inline-end: 1px solid #e0e0e0;
  overflow-y: auto;

  .v-list-item {
    border-radius: 0;

    &:hover {
      background-color: rgba(var(--v-theme-primary), 0.08);
    }
  }
}

.main-content {
  min-block-size: calc(100vh - 64px);
}

.search-filters {
  border-radius: 0;
  border-block-end: 1px solid #e0e0e0;
}

.results-grid {
  background-color: #f8f9fa;
}

.supplier-card {
  display: flex;
  overflow: hidden;
  flex-direction: column;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 15%);
    transform: translateY(-2px);
  }
}

// Responsividade
@media (max-width: 960px) {
  .search-sidebar {
    display: none;
  }

  .main-content {
    inline-size: 100%;
  }
}

// Cores personalizadas
:deep(.v-list-group__header) {
  .v-list-item__prepend {
    .v-icon {
      color: rgb(var(--v-theme-primary));
    }
  }
}

:deep(.v-breadcrumbs) {
  .v-breadcrumbs-item {
    color: rgb(var(--v-theme-primary));
    font-weight: 500;
  }
}

// Ajustes específicos para a paginação
:deep(.v-pagination) {
  .v-pagination__item {
    &--is-active {
      background-color: rgb(var(--v-theme-primary));
      color: white;
    }
  }
}
</style>
